import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Button, Image, Row, Card, Divider, Tooltip, Col, notification } from 'antd';
import { EditOutlined, LeftOutlined, DragOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';

import CreateUpdateCategoryForm from '../../components/CreateUpdateCategoryForm';

import { useFetch } from '../../hooks';
import { LayoutContext } from '../../contexts';
import { URLS } from '../../constants';
import { getImageFallbackUrl, getMenuId } from '../../utils/helpers';
import DndList from '../../components/DndList/DndList';

const { Meta } = Card;

function CategoryDetail(
  {
    // history,
    // match: {
    //   params: { id: categoryId },
    // },
  }
) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const categoryId = id;
  const { setHeaderComponent } = useContext(LayoutContext);
  const [sortMode, setSortMode] = useState(false);
  const [tempSortedList, setTempSortedList] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { doFetch: updateCategoryFetch } = useFetch();
  const { doFetch: uploadCategoryImageFetch } = useFetch();
  const { doFetch: uploadCategoryThumbnailImageFetch } = useFetch();
  const { doFetch: fetchSort } = useFetch();
  const { doFetch: fetchLanguages } = useFetch();
  const { doFetch: fetchCategories } = useFetch();
  const { doFetch: fetchCategoryDetails } = useFetch();

  // Data states
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [languageList, setLanguageList] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load all required data
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);

        const menuId = getMenuId();

        if (!menuId) {
          console.error('Menu ID not found');
          setIsLoading(false);
          return;
        }

        // Load all data in parallel
        const promises = [
          // Languages
          new Promise((resolve, reject) => {
            fetchLanguages({
              url: URLS.languages,
              onSuccess: resolve,
              onError: reject,
            });
          }),

          // Category details (includes products)
          new Promise((resolve, reject) => {
            fetchCategoryDetails({
              url: `${URLS.categoryDetails(categoryId)}?get_products=true`,
              onSuccess: resolve,
              onError: reject,
            });
          }),

          // Categories (for the form, might not be needed but keeping for consistency)
          new Promise((resolve, reject) => {
            fetchCategories({
              url: URLS.getAllCategories(menuId),
              onSuccess: resolve,
              onError: reject,
            });
          }),
        ];

        const [languagesData, categoryDetailsResponse, categoriesResponse] = await Promise.all(promises);

        // Transform category details to match expected format
        const categoryData = categoryDetailsResponse.category;
        if (categoryData && Array.isArray(categoryData.category_translations)) {
          // Create flattened name properties for form fields
          const nameFields = {};
          categoryData.category_translations.forEach(translation => {
            nameFields[`name.${translation.language_id}`] = translation.name_text;
          });

          const transformedCategory = {
            id: categoryData.id,
            ...nameFields, // Include flattened name fields for form
            translations: categoryData.category_translations.map(translation => ({
              lanId: translation.language_id,
              lanName: languagesData.find(lang => lang.id === translation.language_id)?.name || `Language ${translation.language_id}`,
              name: translation.name_text,
            })),
            image: categoryData.image_url,
            thumbnailImage: categoryData.thumbnail_image,
            tags: [],
            productsCount: 0,
          };
          setSelectedCategory(transformedCategory);
        }

        // Transform categories list if available
        let transformedCategories = [];
        if (categoriesResponse.categories) {
          transformedCategories = categoriesResponse.categories.map(cat => ({
            id: cat.id,
            translations: cat.category_translations.map(translation => ({
              lanId: translation.language_id,
              lanName: languagesData.find(lang => lang.id === translation.language_id)?.name || `Language ${translation.language_id}`,
              name: translation.name_text,
            })),
            image: cat.image_url,
            tags: [],
            productsCount: 0,
          }));
          setCategoryList(transformedCategories);
        }

        setLanguageList(languagesData);

        // Extract and transform products from category details response
        if (categoryData && categoryData.products) {
          // Transform products to include name and nameTranslations fields
          const transformedProducts = categoryData.products.map(product => {
            // Find the primary name (prefer first language, fallback to any available translation)
            const primaryTranslation = product.product_translations?.find(
              translation => translation.language_id === languagesData[0]?.id
            ) || product.product_translations?.[0];

            const productName = primaryTranslation?.name_text || 'Unnamed Product';

            // Create nameTranslations HTML
            const nameTranslationsHtml = product.product_translations?.reduce((acc, translation) => {
              const languageName = languagesData.find(lang => lang.id === translation.language_id)?.name || `Language ${translation.language_id}`;
              acc += `${languageName}: ${translation.name_text}<br />`;
              return acc;
            }, '') || 'No translations available';

            return {
              ...product,
              name: productName,
              nameTranslations: nameTranslationsHtml,
            };
          });
          setProductList(transformedProducts);
        } else {
          setProductList([]);
        }

        setHeaderComponent(<b>{`Category ID: ${categoryId}`}</b>);

      } catch (error) {
        console.error('Error loading category detail data:', error);
        setProductList([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();

    return () => {
      setHeaderComponent(null);
    };
  }, [categoryId, fetchLanguages, fetchCategoryDetails, fetchCategories]);

  const uploadCategoryThumbnailImage = async (categoryData, formValues) => {
    if (!formValues?.thumbnailImage?.file?.originFileObj) {
      notification.success({
        placement: 'bottomRight',
        message: 'Category updated successfully.',
        duration: 3,
      });
      // Invalidate categories cache to refetch the list with updated category
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.invalidateQueries({ queryKey: ['category', categoryId] });
      return;
    }

    await uploadCategoryThumbnailImageFetch({
      method: 'FILE_POST',
      url: URLS.uploadCategoryThumbnailImage({ categoryId: categoryId }),
      params: { image: formValues?.thumbnailImage?.file?.originFileObj },
      showSuccessNotification: true,
      successMessage: 'Category updated successfully.',
      onSuccess: () => {
        // Invalidate categories cache to refetch the list with updated category
        queryClient.invalidateQueries({ queryKey: ['categories'] });
        queryClient.invalidateQueries({ queryKey: ['category', categoryId] });
        navigate('/categories');
      },
    });
  };

  const uploadCategoryImage = async (categoryData, formValues) => {
    if (!formValues?.image?.file?.originFileObj) {
      // No main image, try thumbnail image
      uploadCategoryThumbnailImage(categoryData, formValues);
      return;
    }

    await uploadCategoryImageFetch({
      method: 'FILE_POST',
      url: URLS.uploadCategoryImage({ categoryId: categoryId }),
      params: { image: formValues?.image?.file?.originFileObj },
      showSuccessNotification: false,
      onSuccess: () => {
        // After main image upload, try thumbnail image
        uploadCategoryThumbnailImage(categoryData, formValues);
      },
    });
  };

  const updateCategory = async formValues => {
    const translationsObject = languageList.reduce((acc, language) => {
      acc[language.id] = { name_text: formValues[`name.${language.id}`] };
      return acc;
    }, {});

    await updateCategoryFetch({
      url: URLS.updateCategory({ categoryId }),
      method: 'PUT',
      params: { ...translationsObject, tag_ids: formValues.tagIds },
      onSuccess: data => uploadCategoryImage(data, formValues),
    });
  };

  const handleSortChange = newList => {
    setTempSortedList(newList);
    setHasUnsavedChanges(true);
  };

  const handleSaveSortOrder = () => {
    fetchSort({
      url: URLS.productPrioritiesUpdate({ categoryId }),
      method: 'POST',
      params: {
        priorities: tempSortedList.map(product => product.id),
      },
      onSuccess: () => {
        setSortMode(false);
        setHasUnsavedChanges(false);
        setTempSortedList([]);
        // Update the local product list with the new order
        setProductList(tempSortedList);
      },
    });
  };

  const enterSortMode = () => {
    setSortMode(true);
    setTempSortedList([...productList]);
    setHasUnsavedChanges(false);
  };

  const exitSortMode = () => {
    setSortMode(false);
    setTempSortedList([]);
    setHasUnsavedChanges(false);
  };

  if (isLoading) {
    return (
      <Row>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          Loading category details...
        </div>
      </Row>
    );
  }

  return (
    <>
      <Row>
        {selectedCategory?.id && (
          <CreateUpdateCategoryForm
            selectedCategoryItem={selectedCategory}
            tagList={[]} // Tags not currently used in category detail
            categoryList={categoryList}
            languageList={languageList}
            onSubmit={updateCategory}
          />
        )}
      </Row>
      <Divider />
      <div className="m-b-30" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
        {!sortMode ? (
          <Button
            type="primary"
            icon={<DragOutlined />}
            onClick={enterSortMode}
          >
            Sort Products
          </Button>
        ) : (
          <>
            <Button
              type="dashed"
              icon={<LeftOutlined />}
              onClick={exitSortMode}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleSaveSortOrder}
              disabled={!hasUnsavedChanges}
            >
              Save Order
            </Button>
            {hasUnsavedChanges && (
              <span style={{ color: '#faad14', fontSize: '14px' }}>
                You have unsaved changes
              </span>
            )}
          </>
        )}
      </div>
      <Row gutter={[20, 20]} style={{ overflowX: 'auto' }}>
        <DndList
          list={sortMode ? tempSortedList : productList}
          onChange={handleSortChange}
          sortMode={sortMode}
          renderItem={product => (
            <Col {...(sortMode ? { style: { width: '220px' } } : { span: 4 })} key={product.id}>
              <Card
                className="product-card"
                cover={
                  <Image alt="product image" className="product-card-image" src={product.image_url} fallback={getImageFallbackUrl(product.image_url)} />
                }
                actions={[
                  <Tooltip placement="top" title="Update">
                    <Link to={`/product/${product.id}`}>
                      <EditOutlined type="edit" key="edit" />
                    </Link>
                  </Tooltip>,
                ]}
              >
                <Meta
                  title={
                    <Tooltip
                      placement="top"
                      title={
                        <div
                          dangerouslySetInnerHTML={{
                            __html: product.nameTranslations,
                          }}
                        />
                      }
                    >
                      {product.name}
                    </Tooltip>
                  }
                  description={`${product.price} ${product.currency}`}
                />
              </Card>
            </Col>
          )}
        />
      </Row>
    </>
  );
}

export default CategoryDetail;
